"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wrench, Activity, Droplets, CircleCheck as CheckCircle2, TriangleAlert as AlertTriangle, ShieldCheck, ArrowRight, Gauge, Layers, PhoneCall, SearchCheck, Settings2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Separator } from "@/components/ui/separator";
const diagnosticChecks = [{
  id: "cartridge",
  title: "Valve Cartridge & Stem Testing",
  badge: "Step 01",
  summary: "Checking internal seals, ceramic discs, and stem wear.",
  description: "We disassemble the handle and bonnet nut to test internal ceramic disc cartridges or compression stems. Worn rubber seats, mineral calcification, or hairline stem cracks are identified immediately to determine if rebuild or complete replacement is required.",
  indicators: "Persistent base leaks, stiff handle movement, temperature mixing failure",
  tools: "Cartridge puller, micrometer, seat dressing tool"
}, {
  id: "pressure",
  title: "Dynamic Supply Line & PSI Testing",
  badge: "Step 02",
  summary: "Verifying incoming water pressure and flexible riser integrity.",
  description: "We attach calibrated pressure gauges to hot and cold angle stops. We inspect braided stainless steel supply lines for internal lining collapse, micro-corrosion on brass compression nuts, and check that household pressure falls safely within 45 to 65 PSI.",
  indicators: "Low stream volume, banging pipes (water hammer), sweating connectors",
  tools: "Dual-scale pressure gauge, digital flow meter"
}, {
  id: "drainage",
  title: "P-Trap & Tailpiece Flow Inspection",
  badge: "Step 03",
  summary: "Evaluating waste line pitch, venting suction, and slip-joint seals.",
  description: "Under-sink inspection checks the drain tailpiece, pop-up linkage, and P-trap assembly. We inspect rubber gaskets, slip-joint nuts, and perform camera line confirmation if slow drainage persists past the trap weir into the branch arm.",
  indicators: "Standing sink water, sulfur or sewer odors, cabinet moisture",
  tools: "Inspection camera, cleanout auger, seal pressure testers"
}, {
  id: "aeration",
  title: "Aerator & Spray Head Flow Balancing",
  badge: "Step 04",
  summary: "Removing sediment scale and balancing water stream aeration.",
  description: "Pull-down spray heads and aerator nozzles capture loose sediment and hard water buildup from municipal lines. We chemically descale screens, calibrate flow restrictors, and inspect internal check valves against backflow.",
  indicators: "Erratic spray patterns, splashing, sudden drop in output volume",
  tools: "Ultrasonic descaler, flow rate catch bag"
}];
const commonFailures = [{
  title: "Constant Spout Dripping",
  cause: "Degraded cartridge O-rings or cracked ceramic disc plates.",
  fix: "Precision cartridge rebuild or OEM replacement with silicone seal lubrication.",
  severity: "Medium - Wastes 3,000+ gal/year"
}, {
  title: "Cabinet Base Puddling",
  cause: "Degraded escutcheon seal or leaking braided riser line crimp.",
  fix: "Installation of reinforced stainless steel supply lines and high-grade sealants.",
  severity: "High - Risk of subfloor rot"
}, {
  title: "Low Hot-Side Pressure",
  cause: "Mineral debris blockage in shut-off valve or collapsed inner hose.",
  fix: "Quarter-turn ball valve replacement and internal line power-flush.",
  severity: "Moderate - Reduces efficiency"
}, {
  title: "Gurgling Basin Drains",
  cause: "Negative pressure suction from clogged secondary plumbing vent stack.",
  fix: "Mechanical venting adjustment or roof stack augering.",
  severity: "High - Potential gas escape"
}];
const technicalFaqs = [{
  question: "How do your technicians diagnose whether a faucet needs repair or replacement?",
  answer: "We check casting integrity, metal fatigue inside the faucet body, and internal valve chamber scouring. If the brass or composite body is intact, replacing internal cartridges and seals provides 100% restored function at a fraction of fixture replacement cost. If structural corrosion is present, we recommend replacement."
}, {
  question: "What causes under-sink supply valves to seize up when closed?",
  answer: "Standard multi-turn gate valves rely on rubber washers and brass stems that corrode over years of inactivity in hard water. We replace stubborn gate valves with solid brass quarter-turn ball valves that offer zero-leak shutoffs."
}, {
  question: "Can a malfunctioning sink aerator affect whole-house water pressure?",
  answer: "No, an aerator clog is purely localized to that single fixture. If pressure is low across multiple faucets in your Jonesboro home, our diagnostic shifts to the main pressure reducing valve (PRV) or main supply branch."
}, {
  question: "What is your typical turnaround time for sink diagnostic calls?",
  answer: "Our emergency response vehicles arrive fully equipped with OEM cartridges, ball valves, and commercial P-trap components to diagnose and complete most faucet and sink repairs in a single visit."
}];
export default function FaucetDiagnostics() {
  const shouldReduceMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState("cartridge");
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="faucetsinkdiagnostics" data-nav="light" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        {/* Section Header */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="mx-auto max-w-3xl text-center mb-12 md:mb-16">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Badge variant="secondary" className="px-3 py-1 font-semibold text-primary">
              <Activity className="size-3.5 text-primary mr-1" />
              Technical Diagnostic Standards
            </Badge>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground">
            Faucet & Sink Diagnostics & Repair Protocols
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground font-sans leading-relaxed">
            From hairline cartridge fissures to negative-pressure drain anomalies, we use calibrated tools to pinpoint the root cause of kitchen and bathroom sink malfunctions.
          </p>
        </motion.div>

        {/* Diagnostic Steps Tabs */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="mb-16">
          <div className="mb-6 flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-border pb-4">
            <div>
              <h3 className="text-xl md:text-2xl font-default font-bold text-foreground">
                4-Point Diagnostic Workflow
              </h3>
              <p className="text-sm text-muted-foreground">
                Step-by-step technical testing carried out on every service call in Jonesboro.
              </p>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-6">
            <TabsList className="grid grid-cols-2 md:grid-cols-4 w-full h-auto p-1 bg-muted rounded-xl">
              <TabsTrigger value="cartridge" className="py-3 text-xs md:text-sm font-semibold data-[state=active]:bg-background data-[state=active]:text-primary rounded-lg transition-[color,box-shadow]">
                1. Valve Cartridge
              </TabsTrigger>
              <TabsTrigger value="pressure" className="py-3 text-xs md:text-sm font-semibold data-[state=active]:bg-background data-[state=active]:text-primary rounded-lg transition-[color,box-shadow]">
                2. Line Pressure
              </TabsTrigger>
              <TabsTrigger value="drainage" className="py-3 text-xs md:text-sm font-semibold data-[state=active]:bg-background data-[state=active]:text-primary rounded-lg transition-[color,box-shadow]">
                3. P-Trap & Waste
              </TabsTrigger>
              <TabsTrigger value="aeration" className="py-3 text-xs md:text-sm font-semibold data-[state=active]:bg-background data-[state=active]:text-primary rounded-lg transition-[color,box-shadow]">
                4. Aerator Balance
              </TabsTrigger>
            </TabsList>

            {diagnosticChecks.map((item, index) => <TabsContent key={item.id} value={item.id} data-index={index} className="outline-none">
                <Card className="border border-border bg-card shadow-sm">
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 p-6 md:p-8">
                    <div className="lg:col-span-7 flex flex-col justify-between space-y-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <Badge variant="outline" className="border-primary/30 text-primary font-mono text-xs">
                            {item.badge}
                          </Badge>
                          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Diagnostic Procedure
                          </span>
                        </div>
                        <h4 className="text-2xl font-default font-bold text-card-foreground">
                          {item.title}
                        </h4>
                        <p className="mt-1 text-sm font-medium text-primary">
                          {item.summary}
                        </p>
                        <p className="mt-4 text-sm md:text-base text-muted-foreground leading-relaxed">
                          {item.description}
                        </p>
                      </div>

                      <div className="pt-4 border-t border-border grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="bg-background/60 p-3 rounded-lg border border-border">
                          <div className="flex items-center gap-2 text-xs font-semibold text-foreground mb-1">
                            <AlertTriangle className="size-3.5 text-accent" />
                            Primary Symptom Indicators
                          </div>
                          <p className="text-xs text-muted-foreground leading-normal">
                            {item.indicators}
                          </p>
                        </div>
                        <div className="bg-background/60 p-3 rounded-lg border border-border">
                          <div className="flex items-center gap-2 text-xs font-semibold text-foreground mb-1">
                            <Wrench className="size-3.5 text-primary" />
                            Diagnostic Tooling Used
                          </div>
                          <p className="text-xs text-muted-foreground leading-normal">
                            {item.tools}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="lg:col-span-5 flex flex-col justify-between bg-muted/40 p-5 rounded-xl border border-border">
                      <div className="space-y-4">
                        <h5 className="text-sm font-bold uppercase tracking-wider text-foreground flex items-center gap-2">
                          <SearchCheck className="size-4 text-primary" />
                          Evaluation Criteria
                        </h5>
                        <ul className="space-y-2.5 text-xs md:text-sm text-muted-foreground">
                          <li className="flex items-start gap-2">
                            <CheckCircle2 className="size-4 text-chart-4 shrink-0 mt-0.5" />
                            <span>Tolerance testing against factory PSI & GPM ratings</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle2 className="size-4 text-chart-4 shrink-0 mt-0.5" />
                            <span>Thermal imaging check for hidden wall moisture</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle2 className="size-4 text-chart-4 shrink-0 mt-0.5" />
                            <span>Material wear check for dezincification & pitting</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle2 className="size-4 text-chart-4 shrink-0 mt-0.5" />
                            <span>Full joint stress test under 100% active load</span>
                          </li>
                        </ul>
                      </div>

                      <div className="mt-6 pt-4 border-t border-border">
                        <Button asChild variant="default" className="w-full justify-between">
                          <Link to="/contact/">
                            Request Diagnostic Dispatch
                            <ArrowRight className="size-4" />
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              </TabsContent>)}
          </Tabs>
        </motion.div>

        {/* Technical Matrix Grid */}
        <div className="mb-16">
          <div className="mb-8 text-center max-w-2xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-default font-bold text-foreground">
              Direct Failure Modes & Repair Methods
            </h3>
            <p className="text-sm text-muted-foreground mt-2">
              How we isolate specific mechanical issues and apply certified plumbing remedies.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {commonFailures.map((failure, index) => <motion.div key={failure.title} data-index={index} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn}>
                <Card className="h-full border border-border bg-card hover:shadow-md transition-[border-color,box-shadow] duration-200">
                  <CardHeader className="pb-3">
                    <Badge variant="secondary" className="w-fit text-[11px] mb-2 font-mono text-muted-foreground">
                      {failure.severity}
                    </Badge>
                    <CardTitle className="text-lg font-bold text-card-foreground">
                      {failure.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 text-sm">
                    <div>
                      <span className="font-semibold text-foreground text-xs uppercase block tracking-wider mb-1">
                        Root Cause:
                      </span>
                      <p className="text-muted-foreground text-xs leading-relaxed">
                        {failure.cause}
                      </p>
                    </div>
                    <Separator />
                    <div>
                      <span className="font-semibold text-primary text-xs uppercase block tracking-wider mb-1">
                        Repair Method:
                      </span>
                      <p className="text-muted-foreground text-xs leading-relaxed">
                        {failure.fix}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}
          </div>
        </div>

        {/* Diagnostic Equipment & Quality Assurance */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="mb-16 rounded-2xl bg-secondary/50 border border-border p-6 md:p-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-5 relative rounded-xl overflow-hidden shadow-md border border-border bg-card aspect-4/3 flex items-center justify-center">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/plumber_diagnosing_faucet_leak.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/plumber_diagnosing_faucet_leak.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/plumber_diagnosing_faucet_leak.png-wvc-sizes" alt="Plumber diagnosing kitchen sink and faucet lines" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent flex items-end p-4">
                <span className="text-xs font-semibold text-foreground bg-background/90 px-3 py-1 rounded-md border border-border backdrop-blur-xs">
                  Jonesboro On-Site Precision Diagnostic
                </span>
              </div>
            </div>

            <div className="lg:col-span-7 space-y-6">
              <div>
                <Badge variant="default" className="mb-2">
                  Emergency Readiness
                </Badge>
                <h3 className="text-2xl md:text-3xl font-default font-bold text-foreground">
                  Same-Day Repair Solutions with Fully Stocked Units
                </h3>
                <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed">
                  We don&apos;t guess what is causing your sink failure. Our vans carry OEM cartridges from Moen, Delta, Kohler, American Standard, and Grohe, plus industrial-grade P-trap rebuild hardware to restore fixtures in one appointment.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
                    <Gauge className="size-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-foreground">Pressure Regulated</h4>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Supply lines calibrated to avoid thermal and mechanical blowouts.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
                    <ShieldCheck className="size-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-foreground">Warranty-Backed Seals</h4>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      All replacement O-rings and seats match commercial durability specs.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
                    <Layers className="size-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-foreground">Complete System Flush</h4>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      We clean sediment out of the stop valves before securing lines.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
                    <Settings2 className="size-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-foreground">Precision Alignment</h4>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Tailpieces and slip-joints set with correct fall to eliminate odor.
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-2 flex flex-wrap gap-4 items-center">
                <Button asChild size="lg" className="font-semibold shadow-sm">
                  <Link to="/contact/">
                    <PhoneCall className="size-4 mr-2" />
                    Book Faucet Diagnostic
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg">
                  <Link to="/problems/">
                    View Other Common Issues
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Technical FAQ Accordion */}
        <div className="mx-auto max-w-3xl">
          <div className="text-center mb-8">
            <h3 className="text-2xl md:text-3xl font-default font-bold text-foreground">
              Frequently Asked Diagnostic Questions
            </h3>
            <p className="text-sm text-muted-foreground mt-2">
              Clear answers to help you understand your sink and faucet mechanics.
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full border border-border rounded-xl bg-card px-4">
            {technicalFaqs.map((faq, index) => <AccordionItem key={faq.question} value={`faq-${index}`} data-index={index} className="border-b last:border-b-0 border-border">
                <AccordionTrigger className="text-left font-semibold text-foreground py-4 text-sm md:text-base">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-xs md:text-sm text-muted-foreground leading-relaxed pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>
      </div>
    </section>;
}